"use client"

import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"

import {
  WEBSITE_PAGES_BEFORE_PERSONALIZATION,
  type SignupOnboardingPersonalizationSource,
} from "@/lib/onboarding/personalization-source"
import { useTranslations } from "next-intl"


const PERSONALIZATION_PROGRESS_INTERVAL_MS = 3_000
type ProgressKnowledgeSource = SignupOnboardingPersonalizationSource | "knowledge-base"
const TONE_PERSONALIZATION_PROGRESS_MESSAGE =
  "personalizingYourAgentSToneAndPersonality"

const ROLE_PROMPT_PROGRESS_MESSAGES = [
  "loadingYourSelectedRoleInstructions",
  "combiningYourSelectedAgentRoles",
  "structuringYourAgentSResponsibilities",
  "definingResponseRulesAndBoundaries",
  "preparingYourDefaultRolePrompt",
] as const

const PERSONALIZATION_PROGRESS_MESSAGES = [
  "reviewingYourWebsiteContent",
  "extractingBusinessFactsFromYourWebsiteData",
  "identifyingYourProductsServicesAndCustomerNeeds",
  TONE_PERSONALIZATION_PROGRESS_MESSAGE,
  "adaptingResponsesToTheSelectedAgentRole",
  "polishingYourPersonalizedPrompt",
] as const

const SHOPIFY_PERSONALIZATION_PROGRESS_MESSAGES = [
  "readingYourShopifyStore",
  "extractingBusinessFactsFromYourProductsAndPolicies",
  "identifyingYourProductsServicesAndCustomerNeeds",
  TONE_PERSONALIZATION_PROGRESS_MESSAGE,
  "adaptingResponsesToTheSelectedAgentRole",
  "polishingYourPersonalizedPrompt",
] as const

const DOCUMENT_PERSONALIZATION_PROGRESS_MESSAGES = [
  "reviewingYourUploadedDocuments",
  "extractingBusinessFactsFromYourDocuments",
  "identifyingYourProductsServicesAndCustomerNeeds",
  TONE_PERSONALIZATION_PROGRESS_MESSAGE,
  "adaptingResponsesToTheSelectedAgentRole",
  "polishingYourPersonalizedPrompt",
] as const

const KNOWLEDGE_BASE_PROGRESS_MESSAGES = [
  "reviewingYourKnowledgeBase",
  "extractingBusinessFactsFromYourKnowledge",
  ...DOCUMENT_PERSONALIZATION_PROGRESS_MESSAGES.slice(2),
] as const

const SOCIAL_PERSONALIZATION_PROGRESS_MESSAGES = [
  "reviewingYourSocialProfile",
  "extractingBusinessFactsFromYourPublicPosts",
  "identifyingYourProductsServicesAndCustomerNeeds",
  TONE_PERSONALIZATION_PROGRESS_MESSAGE,
  "adaptingResponsesToTheSelectedAgentRole",
  "polishingYourPersonalizedPrompt",
] as const

export function PersonalizationProgress({
  pagesReady,
  websitePagesTotal,
  phase = "personalization",
  knowledgeSource = "website",
}: {
  pagesReady: number
  websitePagesTotal?: number
  phase?: "role-prompt" | "personalization"
  knowledgeSource?: ProgressKnowledgeSource
}) {
  const tUi = useTranslations("Onboarding.PersonalizationProgress")

  const [messageIndex, setMessageIndex] = useState(0)
  const messages = getProgressMessages(phase, knowledgeSource)

  const progressDetail = getProgressDetail(phase, knowledgeSource, pagesReady, websitePagesTotal, tUi)

  useEffect(() => {
    const interval = window.setInterval(() => {
      setMessageIndex(
        (currentIndex) => (currentIndex + 1) % messages.length,
      )
    }, PERSONALIZATION_PROGRESS_INTERVAL_MS)

    return () => window.clearInterval(interval)
  }, [knowledgeSource, messages.length, phase])

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-64 flex-col items-center justify-center gap-4 text-center"
    >
      <div className="rounded-full bg-blue-50 p-4">
        <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
      </div>
      <div className="max-w-md">
        <h2 key={tUi(messages[messageIndex])} className="font-medium">{tUi(messages[messageIndex])}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          <span key={progressDetail}>{progressDetail}</span>
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          <span key={tUi("thisMayTakeAMomentYouCanSafely")}>{tUi("thisMayTakeAMomentYouCanSafely")}</span></p>
      </div>
    </div>
  )
}

function getProgressMessages(
  phase: "role-prompt" | "personalization",
  knowledgeSource: ProgressKnowledgeSource,
) {
  switch (phase) {
    case "role-prompt":
      return ROLE_PROMPT_PROGRESS_MESSAGES
    case "personalization":
      return PERSONALIZATION_MESSAGES_BY_SOURCE[knowledgeSource]
    default: {
      const exhaustivePhase: never = phase
      return exhaustivePhase
    }
  }
}

function getProgressDetail(
  phase: "role-prompt" | "personalization",
  knowledgeSource: ProgressKnowledgeSource,
  pagesReady: number,
  websitePagesTotal: number | undefined,
  tUi: ReturnType<typeof useTranslations<"Onboarding.PersonalizationProgress">>,
) {
  switch (phase) {
    case "role-prompt":
      return tUi("preparingInstructionsFromYourSelectedRoles")
    case "personalization":
      return getPersonalizationDetail(
        knowledgeSource,
        pagesReady,
        websitePagesTotal,
            tUi,
      )
    default: {
      const exhaustivePhase: never = phase
      return exhaustivePhase
    }
  }
}

const PERSONALIZATION_MESSAGES_BY_SOURCE: Record<
  ProgressKnowledgeSource,
  readonly (keyof typeof import("@/translations/en/onboarding.json").Onboarding.PersonalizationProgress)[]
> = {
  website: PERSONALIZATION_PROGRESS_MESSAGES,
  documents: DOCUMENT_PERSONALIZATION_PROGRESS_MESSAGES,
  social: SOCIAL_PERSONALIZATION_PROGRESS_MESSAGES,
  shopify: SHOPIFY_PERSONALIZATION_PROGRESS_MESSAGES,
  "knowledge-base": KNOWLEDGE_BASE_PROGRESS_MESSAGES,
}

function getPersonalizationDetail(
  knowledgeSource: ProgressKnowledgeSource,
  pagesReady: number,
  websitePagesTotal: number | undefined,
  tUi: ReturnType<typeof useTranslations<"Onboarding.PersonalizationProgress">>,
): string {
  switch (knowledgeSource) {
    case "knowledge-base":
      return tUi("personalizingFromSelectedKnowledge")
    case "documents":
      return tUi("uploadedDocumentsAreReady")
    case "shopify":
      return tUi("yourStoreIsReady")
    case "social":
      return pagesReady > 0
        ? tUi("socialProfileKnowledgeIsReady")
        : tUi("importingPublicProfileContent")
    case "website":
      return getWebsiteProgressDetail(pagesReady, websitePagesTotal, tUi)
    default: {
      const exhaustiveSource: never = knowledgeSource
      return exhaustiveSource
    }
  }
}

function getWebsiteProgressDetail(
  pagesReady: number,
  websitePagesTotal: number | undefined,
  tUi: ReturnType<typeof useTranslations<"Onboarding.PersonalizationProgress">>,
) {
  const total = websitePagesTotal ?? WEBSITE_PAGES_BEFORE_PERSONALIZATION
  return tUi("value0OfValue1WebsitePagesReady", { value0: Math.min(pagesReady, total), value1: total })
}
